import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface WizardStepDefinitionDto {
  key: string;
  required: boolean;
  optional: boolean;
  conditionKey: string | null;
  mayAwaitApproval: boolean;
}

export interface WizardStepResultDto {
  key: string;
  status: string;
  error: string | null;
}

export interface WizardRunDto {
  id: string;
  definitionKey: string;
  definitionVersion: number;
  status: string;
  currentStepKey: string;
  steps: WizardStepDefinitionDto[];
  stepResults: WizardStepResultDto[];
  context: Record<string, string>;
  subjectType: string | null;
  subjectId: string | null;
  startedByUserId: string | null;
  completedAtUtc: string | null;
  createdAtUtc: string;
}

export interface WizardDefinitionDto {
  key: string;
  version: number;
  titleKey: string;
  startMode: string;
  cardinality: string;
  audience: string;
  allowsAbandon: boolean;
  gateUntilComplete: boolean;
  requiredPermission: string;
  requiredAction: string;
  steps: WizardStepDefinitionDto[];
  completionRedirectPath: string | null;
}

@Injectable({ providedIn: 'root' })
export class WizardApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/wizards`;

  getGate(): Observable<WizardRunDto | null> {
    return this.http
      .get<WizardRunDto>(`${this.base}/gate`, { observe: 'response' })
      .pipe(map((response: HttpResponse<WizardRunDto>) => (response.status === 204 ? null : response.body ?? null)));
  }

  getRun(runId: string): Observable<WizardRunDto> {
    return this.http.get<WizardRunDto>(`${this.base}/runs/${runId}`);
  }

  submitStep(runId: string, stepKey: string, payloadJson: string): Observable<WizardRunDto> {
    return this.http.post<WizardRunDto>(`${this.base}/runs/${runId}/steps/${encodeURIComponent(stepKey)}`, {
      payloadJson
    });
  }

  startRun(body: {
    definitionKey: string;
    subjectType?: string;
    subjectId?: string;
    initialContext?: Record<string, string>;
  }): Observable<WizardRunDto> {
    return this.http.post<WizardRunDto>(`${this.base}/runs`, body);
  }

  abandonRun(runId: string): Observable<WizardRunDto> {
    return this.http.post<WizardRunDto>(`${this.base}/runs/${runId}/abandon`, {});
  }
}
